import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Play, ChevronLeft, ChevronRight, Lock, Heart } from 'lucide-react';
import { apiCall } from './api';
import { getT } from './i18n';
import CourseOverview from './CourseOverview';
import SupportModal from './SupportModal';

export default function FormationIntro({ onStartTest, onStartFromBeginning, onBack, modules, onModuleClick, lang = 'fr', levelStatus = {}, openAccess = false }) {
  const t = getT(lang);
  const ordered = (modules || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const [pricing, setPricing] = useState({});
  const [supportOpen, setSupportOpen] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  useEffect(() => {
    apiCall('/pricing').then(items => {
      const m = {}; items.forEach(p => { m[p.module_id] = p; }); setPricing(m);
    }).catch(() => {});
  }, []);
  const isLevelUnlocked = (m) => {
    if (openAccess) return true;
    const o = m.order || 0;
    if (o <= 1) return true;
    const prev = ordered.find(x => (x.order || 0) === o - 1);
    if (!prev) return true;
    return !!levelStatus[prev.id]?.is_complete;
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-10" data-testid="formation-intro">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1"><ChevronLeft size={16} />{t('back')}</button>

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-3xl p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 text-[150px] opacity-10 leading-none">🇬🇧</div>
        <div className="text-xs uppercase tracking-wider text-emerald-200 mb-2">{t('fi_eyebrow')}</div>
        <h1 className="text-3xl sm:text-4xl font-black mb-3">{t('fi_title')}</h1>
        <p className="text-white/80 max-w-2xl">{t('fi_subtitle')}</p>
      </div>

      {/* Soutenir — visible dès l'accueil du module (période d'essai, texte en français) */}
      <div className="rounded-2xl p-5 mb-6 bg-gradient-to-br from-rose-600/20 to-pink-700/10 border border-rose-400/25 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="support-banner">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-rose-200 font-bold"><Heart size={18} />Soutenir l’application</div>
          <p className="text-sm text-white/75 mt-1">
            L’application est <strong>gratuite pendant la période d’essai</strong>. Si elle t’aide, soutiens-nous avec le montant de ton choix —
            ça nous motive à ajouter plus de leçons et à l’améliorer selon vos besoins. 🙏
          </p>
        </div>
        <button onClick={() => setSupportOpen(true)} data-testid="open-support"
          className="shrink-0 bg-rose-500 hover:bg-rose-400 text-white font-bold px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition">
          <Heart size={16} />Soutenir
        </button>
      </div>
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />

      {/* Parcours unique FR → EN */}

      {/* Choix : test ou départ — EN AVANT */}
      <h3 className="font-bold mb-3">{t('fi_choose')}</h3>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <motion.button whileHover={{ y: -2 }} onClick={onStartTest} data-testid="choose-leveltest"
          className="text-left rounded-2xl p-5 bg-gradient-to-br from-yellow-600 to-orange-700 group">
          <Brain size={28} className="text-white/80 mb-3" />
          <div className="text-xs text-yellow-200 uppercase tracking-wider">{t('fi_test_badge')}</div>
          <h4 className="text-lg font-bold mt-1">{t('fi_test_title')}</h4>
          <p className="text-sm text-white/80 mt-1">{t('fi_test_desc')}</p>
          <div className="inline-flex items-center gap-1 mt-4 text-sm font-bold bg-white/20 px-3 py-1.5 rounded-full">{t('fi_test_cta')}<ChevronRight size={14} /></div>
        </motion.button>
        <motion.button whileHover={{ y: -2 }} onClick={onStartFromBeginning} data-testid="choose-from-start"
          className="text-left rounded-2xl p-5 bg-gradient-to-br from-emerald-600 to-emerald-800 group">
          <Play size={28} className="text-white/80 mb-3" />
          <div className="text-xs text-emerald-200 uppercase tracking-wider">{t('fi_start_badge')}</div>
          <h4 className="text-lg font-bold mt-1">{t('fi_start_title')}</h4>
          <p className="text-sm text-white/80 mt-1">{t('fi_start_desc')}</p>
          <div className="inline-flex items-center gap-1 mt-4 text-sm font-bold bg-white/20 px-3 py-1.5 rounded-full">{t('fi_start_cta')}<ChevronRight size={14} /></div>
        </motion.button>
      </div>

      {/* Levels overview — clickable to explore each module */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <h3 className="font-bold mb-3 text-sm text-white/80">{t('fi_levels_title')} <span className="text-emerald-300/70 font-normal text-[11px]">— {t('fi_levels_hint')}</span></h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {(showLevels ? ordered : ordered.slice(0, 3)).map(m => {
            const p = pricing[m.id];
            const title = lang === 'en' && m.title_en ? m.title_en : m.title_fr;
            const locked = !isLevelUnlocked(m);
            return (
              <button key={m.id}
                onClick={() => onModuleClick && onModuleClick(m)}
                disabled={!onModuleClick}
                data-testid={`level-card-${m.id}`}
                className={`relative flex items-center gap-3 rounded-lg p-2.5 text-left transition ${locked ? 'bg-slate-900/40 opacity-60 border border-white/10' : p?.is_premium ? 'bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20' : 'bg-slate-900/40 hover:bg-emerald-500/10 hover:border-emerald-400/30 border border-transparent'} ${onModuleClick ? 'cursor-pointer' : 'cursor-default'}`}>
                <span className="text-2xl">{m.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold flex items-center gap-1.5">
                    {title}
                    {locked && <Lock size={11} className="text-white/40 flex-shrink-0" />}
                  </div>
                  <div className="text-[11px] text-white/50">{locked && (m.order || 0) > 1 ? (lang === 'fr' ? 'Termine le niveau précédent' : 'Finish the previous level') : m.level}</div>
                </div>
                {onModuleClick && <ChevronRight size={14} className="text-white/30 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        {ordered.length > 3 && (
          <button
            onClick={() => setShowLevels(v => !v)}
            data-testid="fi-levels-toggle"
            className="mt-3 w-full py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-200 text-sm font-semibold transition">
            {showLevels ? (lang === 'fr' ? 'Voir moins' : 'See less') : (lang === 'fr' ? `Voir plus (${ordered.length - 3})` : `See more (${ordered.length - 3})`)}
          </button>
        )}
      </div>

      {/* Aperçu du cours — objectifs par niveau */}
      <CourseOverview lang={lang} />
    </motion.div>
  );
}
